import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    NgZone,
    Renderer2,
    ViewChild
} from '@angular/core';
import { SettingsService } from '@core/services/settings.service';
import { FormGroup } from '@angular/forms';
import { ProductDetailViewModel, ProductSearchViewModel, RelatedProperties } from '@models/api-search.models';
import { ResizeElementService } from '@core/services/resize-element.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime } from 'rxjs/operators';
import { KeyValue, NgIf, NgFor, NgClass, NgTemplateOutlet, AsyncPipe, KeyValuePipe } from '@angular/common';
import { trackItemByVariantNo } from '@shared/track-by/track-by-variant-no';
import { HasRelatedPropertiesPipe } from '../../product/pipes/has-related-properties.pipe';
import { GetMatchColorPipe } from '../../product/pipes/get-match-color.pipe';
import { GetSelectedVariantPipe } from '../../product/pipes/get-selected-variant.pipe';
import { GetVariantFromRelatedPropertyPipe } from '../../product/pipes/get-variant-from-related-property.pipe';

import { UrlToImagePipe } from '@shared/image/pipes/url-to-image.pipe';
import { SortNumericPropertyPipe } from '@shared/product-details-pipes/sort-numeric-property.pipe';
import { MatchColorPipe } from '@shared/product-details-pipes/match-color.pipe';
import { FilterRelatedColorPipe } from '@shared/product-details-pipes/filter-related-color.pipe';
import { TranslateSafePipe } from '@shared/translate-safe/translate-safe.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { RadioComponent } from '@shared/form-elements/radio/radio.component';
import { SelectionComponent } from '@shared/form-elements/selection/selection.component';
import { ProductImageComponent } from '@shared/image/product-image/product-image.component';
import { SelectionThumbComponent } from '@shared/form-elements/selection-thumb/selection-thumb.component';
import { NgAfterViewInitDirective } from '@shared/ng-after-view-init/ng-after-view-init.directive';
import { PlatformService } from '@core/services/platform.service';
import { keyValueSort } from '@features/product/helper/key-value-sort';

type RelatedPropertyKey = keyof RelatedProperties;

@UntilDestroy()
@Component({
    selector: 'app-product-related-properties',
    templateUrl: './product-related-properties.component.html',
    styleUrls: ['./product-related-properties.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgIf,
        NgFor,
        NgClass,
        NgAfterViewInitDirective,
        SelectionThumbComponent,
        ProductImageComponent,
        NgTemplateOutlet,
        SelectionComponent,
        RadioComponent,
        AsyncPipe,
        KeyValuePipe,
        TranslateModule,
        TranslateSafePipe,
        FilterRelatedColorPipe,
        MatchColorPipe,
        SortNumericPropertyPipe,
        UrlToImagePipe,
        GetVariantFromRelatedPropertyPipe,
        GetSelectedVariantPipe,
        GetMatchColorPipe,
        HasRelatedPropertiesPipe
    ]
})
export class ProductRelatedPropertiesComponent implements AfterViewInit {
    private numOfRows: number = 0;

    public keyValueSort = keyValueSort;
    public trackItemByVariantNo = trackItemByVariantNo;

    public showMoreOpen = false;
    public showMoreButton: boolean = false;
    public numOfHiddenThumbnails = 0;

    @ViewChild('thumbnails') thumbnails: ElementRef<HTMLElement> | undefined;

    @Input() formGroup: FormGroup = new FormGroup({});
    @Input() relatedProperties?: RelatedProperties;
    @Input() variants: ProductSearchViewModel[] = [];
    @Input() selectedVariant: ProductDetailViewModel | null = null;

    constructor(
        public readonly settingsService: SettingsService,
        private readonly platformService: PlatformService,
        private readonly ngZone: NgZone,
        private readonly renderer: Renderer2,
        private readonly resizeElementService: ResizeElementService,
        private readonly cd: ChangeDetectorRef
    ) {}

    ngAfterViewInit() {
        const element = this.thumbnails?.nativeElement;
        if (!element) {
            return;
        }

        // observe the element for resize events we only have to do this once since the element will not change
        // and only be show one at each time
        this.resizeElementService
            .observe('thumbnails', element)
            .pipe(debounceTime(100), untilDestroyed(this))
            .subscribe(entry => {
                this.recalculateThumbnails(entry.target as HTMLDivElement);
            });
    }

    // will be called when the element is re-created again when we switch between variants
    // and the thumbnails are re-rendered again with new data
    onElementViewInit = (element: HTMLElement) => {
        this.recalculateThumbnails(element);
    };

    onShowMore = () => {
        this.showMoreOpen = !this.showMoreOpen;
        this.setMaxHeight(this.thumbnails?.nativeElement);
    };

    trackByRelatedPropertyKey = (index: number, value: KeyValue<unknown, unknown>): string =>
        (value.key as string) || `related-property-${index}`;

    private recalculateThumbnails(element: HTMLElement) {
        const { itemWidth: itemSize, numOfChildren, gap: itemGap, wrapperWidth } = this.getThumbnailInfo(element);
        const property: RelatedPropertyKey = element.getAttribute('property') as RelatedPropertyKey;
        const totalOfThumbs = Math.max(numOfChildren, (this.relatedProperties?.[property]?.length ?? 0) + 1);
        const numOfVisibleColumns = Math.round(wrapperWidth / (itemSize + itemGap));

        this.numOfRows = numOfVisibleColumns > 0 ? Math.ceil(totalOfThumbs / numOfVisibleColumns) : 0;

        if (!totalOfThumbs || !numOfVisibleColumns) {
            return;
        }

        this.numOfHiddenThumbnails = totalOfThumbs - numOfVisibleColumns;

        if (this.platformService.isBrowser) {
            this.showMoreButton =
                totalOfThumbs > 0 && totalOfThumbs > numOfVisibleColumns && this.numOfHiddenThumbnails > 0;
        }

        this.setMaxHeight(element);

        // since we have some delay on the execution of this function, we need to manually trigger change detection
        this.cd.markForCheck();
    }

    private getThumbnailInfo(element?: HTMLElement): {
        wrapperWidth: number;
        itemWidth: number;
        itemHeight: number;
        numOfChildren: number;
        gap: number;
    } {
        if (this.platformService.isServer) {
            return {
                wrapperWidth: 450,
                itemWidth: 66,
                itemHeight: 66,
                numOfChildren: 10,
                gap: 16
            };
        }

        return this.ngZone.runOutsideAngular(() => {
            const wrapperWidth = element?.getBoundingClientRect()?.width || 0;
            const numOfChildren = element?.children?.length || 0;
            const firstChild = element ? (element.children[0] as HTMLElement) : null;
            const itemWidth = firstChild?.getBoundingClientRect()?.width || 66;
            const itemHeight = firstChild?.getBoundingClientRect()?.height || 66;
            const gap = 16;

            return {
                wrapperWidth,
                itemWidth,
                itemHeight,
                numOfChildren,
                gap
            };
        });
    }

    private setMaxHeight(element?: HTMLElement) {
        this.ngZone.runOutsideAngular(() => {
            const { itemHeight, gap } = this.getThumbnailInfo(element);
            let maxHeight = this.numOfRows * itemHeight + this.numOfRows * gap;

            if (!this.showMoreOpen) {
                maxHeight = itemHeight + gap / 2;
            }
            this.renderer.setStyle(element, 'max-height', `${maxHeight}px`);
        });
    }
}
